import React from "react";
import { consolidationViewVersion, readConsolidationView, saveConsolidationView, subscribeConsolidationView } from "../utils/consolidationViewStorage.js";
import useUserUiPreferenceStorageValue from "./useUserUiPreferenceStorageValue.js";

export const ConsolidationViewScope = React.createContext("");

export function useConsolidationPreference(key, fallback = false) {
  const scope = React.useContext(ConsolidationViewScope);
  const raw = useUserUiPreferenceStorageValue("session", `grain-saas:consolidation:view:v1:${scope}`);
  const local = React.useSyncExternalStore(subscribeConsolidationView, consolidationViewVersion, consolidationViewVersion);
  const saved = React.useMemo(() => readConsolidationView(scope), [raw, scope, local]);
  const value = typeof saved[key] === typeof fallback ? saved[key] : fallback;
  const change = (next) => {
    saveConsolidationView(scope, { [key]: next });
  };
  return [value, change];
}

export function useConsolidationScroll(ref, ready, pageKey, scope, skipRestore = false) {
  const useLayoutEffect = typeof window === "undefined" ? React.useEffect : React.useLayoutEffect;
  useLayoutEffect(() => {
    if (!ready || !ref.current) return undefined;
    let target = ref.current.parentElement;
    while (target && !/(auto|scroll)/.test(getComputedStyle(target).overflowY)) target = target.parentElement;
    target ||= document.scrollingElement;
    const key = `scroll:${pageKey}`;
    const saved = readConsolidationView(scope)[key];
    let frame;
    let restored = false;
    // Wait for restored disclosure contents to be laid out before positioning.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        if (!skipRestore && saved && Number.isFinite(saved.top)) target.scrollTop = Math.max(0, saved.top);
        restored = true;
      });
    });
    const record = () => {
      if (restored) saveConsolidationView(scope, { [key]: { top: target.scrollTop } });
    };
    const onScroll = () => {
      if (!restored) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(record);
    };
    const eventTarget = target === document.scrollingElement ? window : target;
    eventTarget.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", record);
    return () => {
      record(); cancelAnimationFrame(frame);
      eventTarget.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", record);
    };
  }, [ready, pageKey, scope, skipRestore, ref]);
}
